import { useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query'
import { fetchPlaylist } from '@/services/fetch-playlist'
import { IPlaylistItem, IPlaylistResponse } from '@/types/playlist'

const createPlaceholderData = (items: number = 8): IPlaylistResponse => {
  const placeholderItem = (index: number): IPlaylistItem => ({
    _id: `__placeholder__-${index}`,
    description: { original: 'placeholder' },
    images: [],
  })

  const placeholderData: IPlaylistItem[] = Array.from({ length: items }, (_, index) =>
    placeholderItem(index)
  )

  return {
    _id: '__placeholder__',
    items: placeholderData,
    title: 'placeholder',
  }
}

interface UsePaginatedPlaylistResult {
  playlistData?: IPlaylistResponse
  items: IPlaylistItem[]
  fetchNextPage: () => Promise<any>
  hasNextPage: boolean
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  isPlaceholderData: boolean
  queryResult: UseInfiniteQueryResult<any, Error>
}

interface UsePaginatedPlaylistProps {
  id: string
  enabled: boolean
  isVisible?: boolean
  options?: {
    params?: Record<string, any>
    placeholderItems?: number
  }
}

const dedupePlaylistItems = (items: IPlaylistItem[]): IPlaylistItem[] => {
  const seenIds = new Set<string>()

  return items.filter((item) => {
    const itemId = item?._id

    if (!itemId || seenIds.has(itemId)) return false

    seenIds.add(itemId)
    return true
  })
}

export function usePaginatedPlaylist({
  id,
  enabled,
  isVisible = false,
  options,
}: UsePaginatedPlaylistProps): UsePaginatedPlaylistResult {
  const queryResult = useInfiniteQuery<any, Error>({
    queryKey: ['playlist', id, options?.params],
    queryFn: ({ pageParam }) => fetchPlaylist({
      id,
      pageParam: pageParam as number,
      options
    }),
    enabled: !!id && enabled,
    // FZ-WEB-NEXTJS-21: refetchOnWindowFocus is conditional on visibility —
    // only playlists currently visible in the viewport will refetch on window
    // focus, preventing the N+1 burst of simultaneous requests from all
    // playlist components when the user switches tabs.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: isVisible,
    placeholderData: {
      pages: [{ data: createPlaceholderData(options?.placeholderItems) }],
      pageParams: [1]
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.data?.pagination

      // 1.Validate presence of pagination info
      if (!pagination) return undefined

      // 2. Normalize values
      const currentPage = Number(pagination.page) || allPages.length
      const totalPages = Number(pagination.pages || pagination.last || 0)
      const nextPageServer = Number(pagination.next)

      // 3. Priority: Use the server's 'next' if it is consistent
      if (nextPageServer > currentPage && (!totalPages || nextPageServer <= totalPages)) {
        return nextPageServer
      }

      // 4. Fallback: Manually increment if we haven't reached the end
      if (totalPages && currentPage < totalPages) {
        return currentPage + 1
      }

      return undefined
    },
  })

  const { data, isLoading, isError, isPlaceholderData, isFetchingNextPage, fetchNextPage, hasNextPage } = queryResult

  // Derive flattened items and top-level playlist data from the query result
  const items = dedupePlaylistItems(data?.pages?.flatMap((p) => p?.data?.items ?? []) ?? [])
  const playlistData = data?.pages?.[0]?.data

  return {
    playlistData,
    items,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    isFetching: isFetchingNextPage,
    isPlaceholderData,
    queryResult,
  }
}